import React, { useState } from "react";
import { Link } from "react-router-dom";
import episodes from "../data/episodes";
import "./EpisodeList.css";

// Lantern SVG icon — used on each card
function LanternIcon({ size = 24 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <rect x="9" y="1" width="6" height="2" rx="1" fill="currentColor" opacity="0.6" />
      <rect x="11" y="3" width="2" height="2" fill="currentColor" opacity="0.4" />
      <rect x="7" y="5" width="10" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <line x1="7" y1="8" x2="17" y2="8" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
      <line x1="7" y1="16" x2="17" y2="16" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
      <ellipse cx="12" cy="12" rx="2.5" ry="3" fill="currentColor" opacity="0.25" />
      <ellipse cx="12" cy="11.5" rx="1.2" ry="1.5" fill="currentColor" opacity="0.5" />
      <rect x="9" y="19" width="6" height="2" rx="1" fill="currentColor" opacity="0.4" />
      <rect x="10" y="21" width="4" height="1.5" rx="0.75" fill="currentColor" opacity="0.3" />
    </svg>
  );
}

// Intensity badge
function IntensityBadge({ level }) {
  const dots = level === "Deep" ? 3 : level === "Moderate" ? 2 : 1;
  return (
    <span className="intensity-badge" aria-label={`Intensity: ${level}`}>
      {Array.from({ length: 3 }).map((_, i) => (
        <span
          key={i}
          className={`dot ${i < dots ? "filled" : "empty"}`}
        />
      ))}
      <span className="intensity-label">{level}</span>
    </span>
  );
}

const archetypeOptions = [...new Set(episodes.map((ep) => ep.archetype))];

export default function EpisodeList({ onSelect }) {
  const [selectedArchetypes, setSelectedArchetypes] = useState([]);

  const toggleArchetype = (a) => {
    setSelectedArchetypes((prev) =>
      prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]
    );
  };

  const filteredEpisodes =
    selectedArchetypes.length === 0
      ? episodes
      : episodes.filter((ep) => selectedArchetypes.includes(ep.archetype));

  return (
    <div className="episode-list-page">
      {/* Header */}
      <header className="site-header">
        <div className="header-lantern">
          <LanternIcon size={36} />
        </div>
        <div className="header-text">
          <h1 className="site-title">Kingdom of Night</h1>
          <p className="site-tagline">
            A quiet place to rest. Choose your guide for tonight.
          </p>
        </div>
        <Link to="/keepers" className="keepers-nav-link">
          Meet the Keepers
        </Link>
      </header>

      {/* Guide filter */}
      <section className="filter-panel" aria-label="Filter episodes">
        <div className="filter-group">
          <span className="filter-label">Guide</span>
          <div className="filter-pills" role="group" aria-label="Filter by guide">
            {archetypeOptions.map((a) => (
              <button
                key={a}
                className={`filter-pill${selectedArchetypes.includes(a) ? " active" : ""}`}
                onClick={() => toggleArchetype(a)}
                aria-pressed={selectedArchetypes.includes(a)}
              >
                {a.replace(/^The /, "")}
              </button>
            ))}
          </div>
        </div>

        {selectedArchetypes.length > 0 && (
          <button
            className="filter-clear"
            onClick={() => setSelectedArchetypes([])}
          >
            Clear
          </button>
        )}
      </section>

      {/* Episode Cards */}
      <main className="episodes-container">
        {filteredEpisodes.length === 0 ? (
          <div className="episodes-empty">
            <p>No episodes match your selection.</p>
            <button
              className="filter-clear"
              onClick={() => setSelectedArchetypes([])}
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="episodes-grid">
            {filteredEpisodes.map((ep, index) => (
              <button
                key={ep.id}
                className="episode-card"
                onClick={() => onSelect(ep)}
                style={{ animationDelay: `${index * 0.12}s` }}
              >
                <div className="card-lantern">
                  <LanternIcon size={20} />
                </div>
                <div className="card-body">
                  <div className="card-meta">
                    <span className="card-theme">{ep.theme}</span>
                    <IntensityBadge level={ep.intensity} />
                  </div>
                  <h2 className="card-title">{ep.title}</h2>
                  <p className="card-description">{ep.description}</p>
                  <div className="card-footer">
                    <span className="card-duration">{ep.duration}</span>
                    <span className="card-cta">Enter →</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="site-footer">
        <p>The Kingdom does not close its gates.</p>
      </footer>
    </div>
  );
}
